import { inject, observer } from 'mobx-react';
import React from 'react';
import DeployMode from '../../../models/deploy/DeployMode';
import { getModelFolders, ModelFolder } from '../../../models/ModelItem';
import Store from '../../../store';
import DeployTabComponent from './DeployTabComponent';

interface MultiEngagementTabProps {
  store?: Store;
  openDeployPopup: (
    deployMode: DeployMode,
    folders: ModelFolder[]
  ) => Promise<void>;
  openImpactPopup: () => Promise<void>;
}

/**
 * Deploy an additional (secondary) account-engagement (MQA) model alongside the
 * standard one — the account-level analogue of MultiFitTab. All of the tenant's
 * MQA models share one <tenant>.yaml and one combined
 * analysis_scoring_domain_engagement table; each secondary adds its own
 * model-id-suffixed columns (logit_<model_id>, signals_<model_id>, …) and
 * coexists with the standard model. Gated server-side by the `multi-account-engagement`
 * LaunchDarkly flag (the deploy is rejected if it's off for the tenant).
 */
export default inject('store')(
  observer(
    class MultiEngagementTab extends React.Component<
      MultiEngagementTabProps,
      Record<string, never>
    > {
      render() {
        const { activeModel, tenantModels } = this.props.store;
        // Flag off → don't offer secondary-engagement deploys on screen at all.
        // The multi-account-engagement flag is a per-tenant value the server
        // stamps onto MQA models. Check BOTH the active model (loaded from
        // /model/:id) and the tenantModels list so it shows regardless of which
        // load path populated the store.
        const flagEnabled =
          activeModel?.multiAccountEngagement ||
          tenantModels.some(
            (model) => model.type === 'mqa' && model.multiAccountEngagement
          );
        if (!flagEnabled) return null;
        // Enabled once a standard (primary) engagement model is live.
        const enabled = tenantModels.some(
          (model) =>
            model.live &&
            model.is_primary &&
            model.type === 'mqa' &&
            model.modelId !== activeModel.modelId
        );
        const folders: ModelFolder[] = getModelFolders(activeModel, {
          forDeployment: true,
        });

        return (
          <DeployTabComponent
            enabled={enabled}
            deployMode="multi"
            folders={folders}
            deployButtonLabel="Deploy as additional Engagement model"
            downloadButtonLabel={(context: string) =>
              `Download ${context} config for secondary engagement`
            }
            disabledMessage="You must first deploy a Primary Account Engagement model."
            title={
              <p>
                <b>Deployment as an additional Account Engagement model </b>(for
                running <b>more than one engagement model</b> on the same
                tenant)
              </p>
            }
            openDeployPopup={this.props.openDeployPopup}
            openImpactPopup={this.props.openImpactPopup}
          >
            <div>
              <ul>
                <li>
                  Deploys this engagement model in parallel with the primary
                  (and any other) live engagement models.
                </li>
              </ul>
              <h6>
                Clicking <b>Deploy as additional Engagement model</b> will:
              </h6>
              <ul>
                <li>
                  Push this model to production; the platform starts scoring
                  every account into this model&apos;s own tables (
                  <code>analysis_scoring_domain_engagement_&lt;model&gt;</code>
                  ).
                </li>
                <li>
                  <b>Not</b> replace any existing model — the primary engagement
                  model and other secondaries keep running unchanged.
                </li>
                <li>
                  Surface the score/segment/signals in integrations where a push
                  configuration is enabled, from the next sync process.
                </li>
              </ul>
              <p className="mt-4 mb-4">
                Make sure of your edits before deploying.
              </p>
            </div>
          </DeployTabComponent>
        );
      }
    }
  )
);
